<!-- =========================================================================================
  File Name: WizardStepResult.vue
  Description: Step 5 - Display clustering result with actions
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-30
========================================================================================== -->

<template>
	<div class="wizard-step-result">
		<div class="step-header">
			<div class="success-icon">
				<feather-icon icon="CheckCircleIcon" svgClasses="w-24 h-24" />
			</div>
			<h2 class="step-title">{{ $t("Wizard.Steps.Result.Success") }}</h2>
		</div>

		<div class="step-content">
			<!-- Results Summary -->
			<div class="results-grid">
				<div class="result-card primary">
					<div class="card-icon">
						<feather-icon icon="LayersIcon" svgClasses="w-8 h-8" />
					</div>
					<div class="card-content">
						<span class="card-value">{{ result.totalClusters || 0 }}</span>
						<span class="card-label">{{ $t("Clustering.Clusters") }}</span>
					</div>
				</div>

				<div class="result-card success">
					<div class="card-icon">
						<feather-icon icon="GridIcon" svgClasses="w-8 h-8" />
					</div>
					<div class="card-content">
						<span class="card-value">{{ result.totalMicroClusters * result.totalClusters || 0 }}</span>
						<span class="card-label">{{ $t("Clustering.MicroClusters") }}</span>
					</div>
				</div>

				<div class="result-card info">
					<div class="card-icon">
						<feather-icon icon="MapPinIcon" svgClasses="w-8 h-8" />
					</div>
					<div class="card-content">
						<span class="card-value">{{ result.loadedPoints.length || 0 }}</span>
						<span class="card-label">{{ $t("Clustering.TotalPoints") }}</span>
					</div>
				</div>

				<div class="result-card warning">
					<div class="card-icon">
						<feather-icon icon="PackageIcon" svgClasses="w-8 h-8" />
					</div>
					<div class="card-content">
						<span class="card-value">{{ result.totalDemand || 0 }}</span>
						<span class="card-label">{{ $t("Clustering.TotalDemand") }}</span>
					</div>
				</div>
			</div>
			<div v-if="isClusteringProcessing" class="status-alert">
				<vs-alert color="warning" icon-pack="feather" icon="icon-clock" active="true">
					<div class="alert-content">
						<div class="alert-header">
							<strong>{{ $t("Clustering.CreatingCluster") }}</strong>
							<vs-chip color="warning" class="ml-2">
								<feather-icon icon="LoaderIcon" svgClasses="w-4 h-4" class="mr-1" />
								{{ $t("Clustering.Processing") }}
							</vs-chip>
						</div>
						<p class="alert-message">{{ $t("Clustering.CreatingClusterMessageBackground") }}</p>
					</div>
				</vs-alert>
			</div>
			<!-- Actions -->
			<div class="result-actions">
				<vx-card>
					<h3 class="actions-title">{{ $t("Wizard.Steps.Result.NextSteps") }}</h3>
					<p class="actions-description">{{ $t("Wizard.Steps.Result.ActionsHelp") }}</p>

					<div class="actions-grid">
						<vs-button
							color="primary"
							type="gradient"
							icon-pack="feather"
							icon="icon-eye"
							size="large"
							class="action-button"
							:disabled="!isClusteringReady"
							@click="$emit('view-on-map')"
						>
							{{ $t("Clustering.ViewOnMap") }}
						</vs-button>

						<vs-button
							color="success"
							type="gradient"
							icon-pack="feather"
							icon="icon-download"
							size="large"
							class="action-button"
							:disabled="!isClusteringReady"
							@click="$emit('export-result')"
						>
							{{ $t("Export") }}
						</vs-button>

						<vs-button
							color="warning"
							type="gradient"
							icon-pack="feather"
							icon="icon-refresh-cw"
							size="large"
							class="action-button"
							@click="$emit('create-another')"
						>
							{{ $t("Wizard.Steps.Result.CreateAnother") }}
						</vs-button>

						<vs-button
							color="dark"
							type="border"
							icon-pack="feather"
							icon="icon-list"
							size="large"
							class="action-button"
							@click="$emit('view-list')"
						>
							{{ $t("Clustering.ClusterList") }}
						</vs-button>
					</div>
				</vx-card>
			</div>

			<!-- Info Panel -->
			<div class="info-panel">
				<vs-alert color="primary" icon-pack="feather" icon="icon-info">
					<div class="info-content">
						<strong>{{ $t("Wizard.Steps.Result.InfoTitle") }}</strong>
						<ul class="info-list">
							<li>{{ $t("Wizard.Steps.Result.Info1") }}</li>
							<li>{{ $t("Wizard.Steps.Result.Info2") }}</li>
							<li>{{ $t("Wizard.Steps.Result.Info3") }}</li>
						</ul>
					</div>
				</vs-alert>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: "WizardStepResult",
	props: {
		wizardData: {
			type: Object,
			required: true,
		},
	},
	computed: {
		result() {
			return this.wizardData || {};
		},
		totalDemand() {
			return this.wizardData.selectedPoints.reduce((sum, p) => sum + (p.demand || 0), 0);
		},
		isClusteringReady() {
			return (
				this.wizardData.clusteringResult &&
				this.wizardData.clusteringResult.id &&
				this.wizardData.clusteringResult.status === "completed"
			);
		},
		isClusteringProcessing() {
			// Check if clustering is being created in background
			return this.wizardData.clusteringResult && this.wizardData.clusteringResult.status === "processing";
		},
	},
};
</script>

<style lang="scss" scoped>
.wizard-step-result {
	.step-header {
		text-align: center;
		margin-bottom: 48px;

		.success-icon {
			color: #4caf50;
			margin-bottom: 24px;
			animation: scale-in 0.5s ease;
		}

		.step-title {
			font-size: 1.75rem;
			font-weight: 700;
			color: #4caf50;
			margin-bottom: 8px;
		}

		.step-description {
			font-size: 1rem;
			color: #666;
		}
	}

	.step-content {
		.results-grid {
			display: grid;
			grid-template-columns: repeat(2, 1fr);
			gap: 16px;
			margin-bottom: 24px;

			.result-card {
				background: linear-gradient(135deg, var(--card-color-1) 0%, var(--card-color-2) 100%);
				color: white;
				padding: 32px 24px;
				border-radius: 16px;
				display: flex;
				flex-direction: column;
				align-items: center;
				text-align: center;
				box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
				animation: slide-up 0.5s ease;

				&.primary {
					--card-color-1: #667eea;
					--card-color-2: #764ba2;
				}

				&.success {
					--card-color-1: #56ab2f;
					--card-color-2: #a8e063;
				}

				&.info {
					--card-color-1: rgba(var(--vs-success), 1);
					--card-color-2: #21cbf3;
				}

				&.warning {
					--card-color-1: #f2994a;
					--card-color-2: #f2c94c;
				}

				.card-icon {
					margin-bottom: 16px;
					opacity: 0.9;
				}

				.card-content {
					display: flex;
					flex-direction: column;

					.card-value {
						font-size: 3rem;
						font-weight: 700;
						line-height: 1;
						margin-bottom: 12px;
					}

					.card-label {
						font-size: 0.9rem;
						opacity: 0.9;
					}
				}
			}
		}

		.result-actions {
			margin-bottom: 32px;

			.actions-title {
				font-size: 1.75rem;
				margin-bottom: 12px;
			}

			.actions-description {
				color: #666;
				margin-bottom: 24px;
			}

			.actions-grid {
				display: grid;
				grid-template-columns: repeat(2, 1fr);
				gap: 16px;

				.action-button {
					width: 100%;
					height: 60px;
					font-size: 1.1rem;
				}
			}
		}

		.info-panel {
			.info-content {
				.info-list {
					margin-top: 12px;
					padding-left: 20px;

					li {
						margin-bottom: 8px;
					}
				}
			}
		}

		.status-alert {
			margin-bottom: 24px;

			.alert-content {
				.alert-header {
					display: flex;
					align-items: center;
					margin-bottom: 8px;
				}

				.alert-message {
					margin-top: 8px;
					opacity: 0.9;
				}
			}
		}

		.action-button {
			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}
	}
}

@keyframes scale-in {
	from {
		transform: scale(0);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes slide-up {
	from {
		transform: translateY(30px);
		opacity: 0;
	}
	to {
		transform: translateY(0);
		opacity: 1;
	}
}

@media screen and (max-width: 1024px) {
	.wizard-step-result {
		.results-grid {
			grid-template-columns: repeat(2, 1fr) !important;
		}
	}
}

@media screen and (max-width: 768px) {
	.wizard-step-result {
		.results-grid {
			grid-template-columns: 1fr !important;
		}

		.result-actions .actions-grid {
			grid-template-columns: 1fr !important;
		}
	}
}
</style>
